import { Injectable } from '@angular/core';
import { StageService } from '../stage.service';
import { Edge } from '../railing';
import { DrawingsHolderService } from './drawings-holder.service';
import {
  Floor,
  Stairs,
  Post,
  Edge as EdgeMesh,
  Dimensions,
  PolyLine,
  Arc,
  Circle,
  Rectangle,
  Angle,
  Text,
  DoublePost,
  TerminationPost,
  SelectablePicket,
  SingleCornerPost
} from './object3D';
import { PostFactoryService } from './post-factory.service';
import { Border } from './object3D/Floor/border';
import { ProjectManagerService } from '../project-manager.service';
import { Size } from './object3D/Dimensions/Size';
import { TextArrow } from './object3D/Text/TextArrow';
import { start } from 'repl';
import { DrawTitleBlockComponent } from './components/DrawTitleBlockComponent';

declare const THREE: any;

interface ICornerPost {
  x: number;
  z: number;
  non90: boolean;
  rotation: number;
  visible?: boolean;
}

interface IArrangeConfig {
  floorsPosts: Feeney.IPost[][][];
  floors: any[];
  stairsPosts: Feeney.IAddHandrailResult[];
  floorsCornerPosts: ICornerPost[][];
  verticalPickets: any[];
}

@Injectable()
export class PlanBuilderService {
  constructor(
    private stage: StageService,
    private drawings: DrawingsHolderService,
    private postFactoryService: PostFactoryService,
    private projectManager: ProjectManagerService,
    private DrawTitleBlock: DrawTitleBlockComponent
  ) {}

  createPickets(positions, edges, stairsEdges) {
    const result = [];

    positions.forEach(p => {
      let Edge = null;
      let labelPos = 'Center';
      let label = 'PK';
      edges.forEach(edge => {
        const start = edge.edge.start;
        const end = edge.edge.end;

        if (
          Math.abs(
            start.distanceTo(end) -
              start.distanceTo(p.clone().setY(0)) -
              end.distanceTo(p.clone().setY(0))
          ) < 5
        ) {
          Edge = edge.edge;
          label = 'PK';
          let dir = new THREE.Vector3().add(end).sub(start);
          dir.set(
            dir.x * Math.cos(Math.PI / 4) - dir.z * Math.sin(Math.PI / 4),
            dir.y,
            dir.z * Math.cos(Math.PI / 4) + dir.x * Math.sin(Math.PI / 4)
          );
          if (dir.z > 0) {
            if (dir.x > 0) {
              //2
              labelPos = 'Below';
            } else {
              //3
              labelPos = 'Left Side';
            }
          } else {
            if (dir.x > 0) {
              //1
              labelPos = 'Right Side';
            } else {
              //4
              labelPos = 'Above';
            }
          }
        }
      });
      stairsEdges.forEach(edge => {
        const start = edge.edge.start;
        const end = edge.edge.end;
        if (
          Math.abs(
            start.distanceTo(end) -
              start.distanceTo(p.clone().setY(0)) -
              end.distanceTo(p.clone().setY(0))
          ) < 5
        ) {
          Edge = edge.edge;
          label = 'PKS';
          let dir = new THREE.Vector3().add(end).sub(start);
          if (!edge.isRight) {
            dir.negate();
          }
          dir.set(
            dir.x * Math.cos(Math.PI / 4) - dir.z * Math.sin(Math.PI / 4),
            dir.y,
            dir.z * Math.cos(Math.PI / 4) + dir.x * Math.sin(Math.PI / 4)
          );

          if (dir.z > 0) {
            if (dir.x > 0) {
              //2
              labelPos = 'Below';
            } else {
              //3
              labelPos = 'Left Side';
            }
          } else {
            if (dir.x > 0) {
              //1
              labelPos = 'Right Side';
            } else {
              //4
              labelPos = 'Above';
            }
          }
        }
      });
      const s = this.postFactoryService.createPicket({ x: p.x, z: p.z }, Edge, label, labelPos);

      console.log(Edge);
      result.push(s);
    });
    return result;
  }

  private createPosts(floorsPosts: Feeney.IPost[][][]): Post[] {
    const result = [];

    floorsPosts.forEach(floorPosts =>
      floorPosts.forEach(edgePosts => {
        const first = edgePosts[0];
        const last = edgePosts[edgePosts.length - 1];
        const firstPosition = new THREE.Vector3(first.x, 0, first.z);
        const lastPosition = new THREE.Vector3(last.x, 0, last.z);
        const edge = new Edge(firstPosition, lastPosition);

        // edgePosts.forEach((post) => {
        //     const s = this.createSphere(0xffff00);
        //     s.position.set(0, 0, 0);
        //     this.drawings.canvas.add(s);
        //   }
        // );

        edgePosts
          .filter(p => !p.corner || (p.isTerminationPost && p.corner))
          .forEach(post => {
            result.push(this.postFactoryService.create(post, edge));
          });
      })
    );

    return result;
  }

  private createCornerPostsForFloors(floorsPosts: ICornerPost[][]) {
    const result = [];

    floorsPosts.forEach(floorPosts => {
      floorPosts.forEach((post, i) => {
        if (post.visible) {
          const nextIndex = i + 1 === floorPosts.length ? 0 : i + 1;
          const edge = new Edge(post, floorPosts[nextIndex]);
          result.push(
            this.postFactoryService.create(
              { ...post, corner: true, isTerminationPost: false },
              edge
            )
          );
        }

        // const s = this.createSphere(0xff0000);
        // s.position.set(post.x, 0, post.z);
        // this.drawings.canvas.add(s);
      });
    });
    return result;
  }

  private createFloors(floors: any[]): any[] {
    return floors.map(floor => {
      const mesh = new Floor(floor.userData.points);

      for (let i = 0; i < floor.userData.points.length; i++) {
        const firstPoint = floor.userData.points[i];
        const secondPoint = floor.userData.points[(i + 1) % floor.userData.points.length];

        const fPoint = new THREE.Vector3(firstPoint.x, 0, firstPoint.y);
        const sPoint = new THREE.Vector3(secondPoint.x, 0, secondPoint.y);
        const offset = new THREE.Vector3(sPoint.z - fPoint.z, 0, fPoint.x - sPoint.x).setLength(15);
        const pos = fPoint.clone().add(offset);

        const project = this.projectManager.getCurrentProject();
        // if (!(project.draw_options && project.draw_options.items
        //   && project.draw_options.items.items)) {
        const object = new Dimensions();

        object.position.copy(pos);

        this.drawings.canvas.add(object);

        object.update(fPoint, sPoint);

        object.Length = fPoint.distanceTo(sPoint);
        // }
      }

      mesh.position.copy(floor.position);
      // mesh.visible = false;
      return mesh;
    });
  }

  private createBorders(floors: any[]): any[] {
    return floors.map(floor => {
      const mesh = new Border(floor.userData.points);

      mesh.position.copy(floor.position);
      // mesh.visible = false;
      return mesh;
    });
  }

  private collectStepsObjects(stair): any[] {
    const steps = [];
    stair.traverse(obj => {
      if (obj.name === 'Steps') {
        steps.push(obj);
      }
    });
    return steps;
  }

  private createStairs(stairs: any[]) {
    const rotation = new THREE.Quaternion();
    const position = new THREE.Vector3();
    return stairs.map(stair => {
      stair.updateMatrixWorld();
      stair.getWorldQuaternion(rotation);
      stair.getWorldPosition(position);
      const { width } = stair.userData.config;
      const steps = this.collectStepsObjects(stair);
      const mesh = new Stairs(steps, width);
      mesh.position.copy(position);
      mesh.quaternion.copy(rotation);
      // mesh.visible = false;
      return mesh;
    });
  }

  private addStairsPosts(data: Feeney.IAddHandrailResult[]): Post[] {
    const result = [];

    data.forEach(item => {
      item.edgePosts.forEach(edgePost => {
        edgePost.postData.forEach(post => {
          result.push(this.postFactoryService.create(post, edgePost.edge));
        });
      });
    });

    return result;
  }

  private createEdges(floorsPosts: Feeney.IPost[][][]): EdgeMesh[] {
    const result = [];

    floorsPosts.forEach(floorPosts =>
      floorPosts.forEach(edgePosts => {
        const first = edgePosts[0];
        const last = edgePosts[edgePosts.length - 1];
        const firstPosition = new THREE.Vector3(first.x, 0, first.z);
        const lastPosition = new THREE.Vector3(last.x, 0, last.z);

        const edge = new Edge(firstPosition, lastPosition);
        const mesh = new EdgeMesh(edge.length, edge);
        mesh.position.copy(firstPosition);
        mesh.rotation.y = edge.rotationAngle;
        result.push(mesh);
      })
    );

    return result;
  }

  private createStairsEdges(stairsPosts: Feeney.IAddHandrailResult[]): EdgeMesh[] {
    const result = [];
    stairsPosts.forEach(stairsPosts => {
      let count = 0;
      if (stairsPosts.edgePosts.length === 2) {
        count++;
      }
      stairsPosts.edgePosts.forEach((edgePost: Feeney.IEdgePosts) => {
        const edge = new Edge(edgePost.edge.start, edgePost.edge.end);
        const mesh = new EdgeMesh(edge.length, edge);
        mesh.position.copy(edgePost.edge.start);
        mesh.rotation.y = edge.rotationAngle;
        if (stairsPosts.edgePosts.length === 7 && count === 3) {
          count++;
        }
        if (count % 2) {
          mesh.isRight = true;
        } else {
          mesh.isRight = false;
        }
        result.push(mesh);
        count++;
      });
    });
    return result;
  }

  createSphere(color) {
    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(2),
      new THREE.MeshBasicMaterial({ color, depthWrite: false, depthTest: false })
    );
    sphere.renderOrder = 5;
    sphere.name = 'marker';
    return sphere;
  }

  excludeAddedPostsAndPicketsFromConfig(config, savedItems) {
    savedItems.forEach(item => {
      if (item.type === 'Post') {
        // exclude floor posts
        config.floorsPosts.forEach((flpa, idx) => {
          flpa.forEach((pa, paidx) => {
            config.floorsPosts[idx][paidx] = pa.filter(p => {
              return !(p.x === item.position.x && p.z === item.position.z);
            });
          });
        });

        // exclude corner posts
        config.floorsCornerPosts.forEach((flcpa, idx) => {
          config.floorsCornerPosts[idx] = flcpa.filter(p => {
            return !(p.x === item.position.x && p.z === item.position.z);
          });
        });

        // exclude stair posts
        config.stairsPosts.forEach((sp, idx) => {
          sp.edgePosts.forEach((edgep, edgeidx) => {
            config.stairsPosts[idx].edgePosts[edgeidx].postData = edgep.postData.filter(pd => {
              return !(pd.x === item.position.x && pd.z === item.position.z);
            });
          });
        });
      }

      if (item.type === 'Picket') {
        config.verticalPickets = config.verticalPickets.filter(vp => {
          return !(vp.x === item.position.x && vp.z === item.position.z);
        });
      }
    });
  }

  build(config: IArrangeConfig) {
    const canvas = this.drawings.canvas;
    const project = this.projectManager.getCurrentProject();

    const stairs = this.stage.getStairs();
    const stairsBlueprints = this.createStairs(stairs);
    canvas.add(...stairsBlueprints);

    const tierSteps = this.stage.getTierSteps();
    const tierStepsBlueprints = this.createStairs(tierSteps);
    canvas.add(...tierStepsBlueprints);

    const floors = this.createFloors(config.floors);
    canvas.add(...floors);

    const floorBorders = this.createBorders(config.floors);
    canvas.add(...floorBorders);

    const edges = this.createEdges(config.floorsPosts);
    canvas.add(...edges);

    const stairsEdges = this.createStairsEdges(config.stairsPosts);
    canvas.add(...stairsEdges);
    if (project.draw_options && project.draw_options.items && project.draw_options.items.items) {
      this.createSavedItems(project.draw_options.items.items);
      if (project.draw_options.items.activeTB) {
        this.DrawTitleBlock.addSavedTitleBlock(project.draw_options.titleBlockOptions);
      }
      this.excludeAddedPostsAndPicketsFromConfig(config, project.draw_options.items.items);
    }
    //  else {
    //   this.createPostsAndPickets(config, canvas, edges, stairsEdges);
    // }

    this.createPostsAndPickets(config, canvas, edges, stairsEdges);
  }

  createPostsAndPickets(config, canvas, edges, stairsEdges) {
    const posts = this.createPosts(config.floorsPosts);
    // canvas.add(...posts);

    const stairsPosts = this.addStairsPosts(config.stairsPosts);
    // canvas.add(...stairsPosts);

    const floorsCornerPosts = this.createCornerPostsForFloors(config.floorsCornerPosts);
    // canvas.add(...floorsCornerPosts);

    const verticalPickets = this.createPickets(config.verticalPickets, edges, stairsEdges);
    canvas.add(...verticalPickets);
  }

  createSavedItems(items) {
    if (items.length) {
      items.forEach(item => {
        // Items created at step 3 only

        if (item.type === 'PolyLine') {
          let obj = new PolyLine();
          obj.rebuild(item);
          this.drawings.canvas.add(obj);
        }
        if (item.type === 'Arc' && item.points.length === 3) {
          let arc = new Arc();
          arc.position.set(item.position.x, item.position.y, item.position.z);
          item.points.forEach(p => {
            arc.addPoint(new THREE.Vector2(p.x, p.y));
          });
          let color = new THREE.Color(item.color);
          arc.setColor(color);
          this.drawings.canvas.add(arc);
        }
        if (item.type === 'Circle') {
          let circle = new Circle();
          circle.position.set(item.position.x, item.position.y, item.position.z);
          circle.update(item.width, item.height);
          let color = new THREE.Color(item.color);
          circle.setColor(color);
          this.drawings.canvas.add(circle);
        }
        if (item.type === 'Rectangle') {
          let rectangle = new Rectangle();
          rectangle.position.set(item.position.x, item.position.y, item.position.z);
          rectangle.update(item.width, item.height);
          let color = new THREE.Color(item.color);
          rectangle.setColor(color);
          this.drawings.canvas.add(rectangle);
        }
        if (item.type === 'Angle') {
          let angle = new Angle();
          angle.position.set(item.position.x, item.position.y, item.position.z);
          let startPoint = new THREE.Vector3(
            item.startPoint.x,
            item.startPoint.y,
            item.startPoint.z
          );
          let endPoint = new THREE.Vector3(item.endPoint.x, item.endPoint.y, item.endPoint.z);
          angle.update(startPoint, endPoint);
          let color = new THREE.Color(item.color);
          angle.setColor(color);
          this.drawings.canvas.add(angle);
        }
        if (item.type === 'Text') {
          let text = new Text();
          text.config = item.config;
          text.position.set(item.position.x, item.position.y, item.position.z);
          text.scaleCor = item.scaleCor;
          let arrow = text.children.filter(ch => ch instanceof TextArrow)[0];
          if (item.startPoint && item.endPoint && arrow) {
            let startPoint = new THREE.Vector3(
              item.startPoint.x,
              item.startPoint.y,
              item.startPoint.z
            );
            let endPoint = new THREE.Vector3(item.endPoint.x, item.endPoint.y, item.endPoint.z);
            arrow.update(startPoint, endPoint);
          }
          let image = new Image();
          image.src = item.imageSrc;
          image.onload = function () {
            text.update(image);
          };
          this.drawings.canvas.add(text);
        }

        // Next items could be added from steps 1 or 2

        if (item.type === 'Dimensions') {
          let dimensions = new Dimensions();
          dimensions.modifiedAtStep3 = true;
          dimensions.position.set(item.position.x, item.position.y, item.position.z);
          let startPoint = new THREE.Vector3(
            item.startPoint.x,
            item.startPoint.y,
            item.startPoint.z
          );
          let endPoint = new THREE.Vector3(item.endPoint.x, item.endPoint.y, item.endPoint.z);
          dimensions.update(startPoint, endPoint);
          const size = dimensions.children.filter(ch => ch instanceof Size)[0];
          size.updateText(item.text);
          let color = new THREE.Color(item.color);
          dimensions.setColor(color);
          this.drawings.canvas.add(dimensions);
        }
        if (item.type === 'DoublePost') {
          const im = new THREE.ImageLoader().load('assets/images/scene-plan/c.png', im => {
            let post = new DoublePost(im);
            post.modifiedAtStep3 = true;

            post.position.set(item.position.x, item.position.y, item.position.z);
            post.rotation.y = item.rotation.y;
            post.scaleCor = item.scaleCor;
            post.update();
            post.children
              .filter(ch => ch.name === 'text')
              .forEach(ch => {
                ch.rotation.y -= post.rotation.y;
              });
            this.drawings.canvas.add(post);
          });
        }
        if (item.type === 'TerminationPost') {
          let post;
          if (item.text) {
            post = new TerminationPost(item.text);
            if (item.labelPos) {
              post.labelPos = item.labelPos;
              post.children
                .filter(ch => ch.name === 'textPost')
                .forEach(ch => {
                  ch.labelPos = item.labelPos;
                });
            }
          } else {
            post = new TerminationPost();
          }

          post.modifiedAtStep3 = true;
          post.position.set(item.position.x, item.position.y, item.position.z);
          post.rotation.y = item.rotation.y;
          post.update();

          post.children
            .filter(ch => ch.name === 'textPost')
            .forEach(ch => {
              ch.rotation.y -= post.rotation.y;
              ch.setLabelPos();
            });
          this.drawings.canvas.add(post);
        }
        if (item.type === 'Picket') {
          let picket;
          if (item.imageSrc && item.typeP.postType !== 'TP') {
            const im = new THREE.ImageLoader().load(
              'assets/images/scene-plan/' + item.typeP.postType.toLowerCase() + '.png',
              im => {
                picket = new SelectablePicket();
                picket.modifiedAtStep3 = true;
                picket.addLabel(item.typeP, im);
                picket.position.set(item.position.x, item.position.y, item.position.z);
                picket.rotation.y = item.rotation.y;
                picket.children
                  .filter(ch => ch.name === 'text')
                  .forEach(ch => {
                    ch.rotation.y -= picket.rotation.y;
                  });

                this.drawings.canvas.add(picket);
              }
            );
          } else {
            picket = new SelectablePicket();
            picket.modifiedAtStep3 = true;
            picket.position.set(item.position.x, item.position.y, item.position.z);
            picket.rotation.y = item.rotation.y;
            if (
              item.typeP.postType === 'TP' &&
              item.text !== '' &&
              item.typeP.labelPosition !== 'Center'
            ) {
              picket.addEditableLabel(
                { postType: item.typeP.postType, labelPosition: item.typeP.labelPosition },
                item.text
              );
            }
            this.drawings.canvas.add(picket);
          }
        }
        if (item.type === 'Post') {
          const im = new THREE.ImageLoader().load(
            'assets/images/scene-plan/' + item.typeP.postType.toLowerCase() + '.png',
            im => {
              let post = new SingleCornerPost(item.typeP, im);
              post.modifiedAtStep3 = true;

              post.position.set(item.position.x, item.position.y, item.position.z);
              post.rotation.y = item.rotation.y;
              post.scaleCor = item.scaleCor;
              post.children
                .filter(ch => ch.name === 'text')
                .forEach(ch => {
                  ch.rotation.y -= post.rotation.y;
                });
              post.setLabelPos();
              post.update();

              this.drawings.canvas.add(post);
            }
          );
        }
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/services/proTools/plan-builder.service.ts